import { useState, useEffect, useMemo } from "react"
import { useNavigate } from 'react-router-dom'
import { Eye, User, Filter, X, Plus, Check, Heart } from "lucide-react"
import { createPortal } from 'react-dom'
import { authService } from "../../services/authService"
import { companyService } from "../../services/companyService"

const TalentSearch = ({ resources: propResources }) => {
  const [allResources, setAllResources] = useState(propResources || [])
  const [selected, setSelected] = useState(null)

  // Initialize selection from sessionStorage if available
  const [selectedResourceIds, setSelectedResourceIds] = useState(() => {
    try {
      const saved = sessionStorage.getItem('selectedBenchResourceIds')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch (e) {
      return new Set()
    }
  })

  // Persist selection to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('selectedBenchResourceIds', JSON.stringify([...selectedResourceIds]))
  }, [selectedResourceIds])

  // Favorites Selection State
  const [favoriteResourceIds, setFavoriteResourceIds] = useState(() => {
    try {
      const saved = localStorage.getItem('favoriteBenchResourceIds')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch (e) {
      return new Set()
    }
  })

  const [showFavorites, setShowFavorites] = useState(false)
  const [viewOnlyFavorites, setViewOnlyFavorites] = useState(false)

  // Persist favorites to localStorage
  useEffect(() => {
    localStorage.setItem('favoriteBenchResourceIds', JSON.stringify([...favoriteResourceIds]))
  }, [favoriteResourceIds])

  const [loading, setLoading] = useState(!propResources)
  const [modalLoading, setModalLoading] = useState(false)

  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 12,
    total_pages: 1,
    total_count: 0
  })

  // Filter states
  const [filters, setFilters] = useState({
    location: '',
    experience: '',
    rating: '',
    technologies: '',
    skills: '',
    cost_per_hour: '',
    availability: '',
    department: '',
    designation: ''
  })

  const [detailedResource, setDetailedResource] = useState(null)

  const toggleSelection = (e, id) => {
    e.stopPropagation()
    setSelectedResourceIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleFavorite = (e, id) => {
    e.stopPropagation()
    setFavoriteResourceIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Helper to remove placeholder/static entries like "john deo"
  const removePlaceholderResources = (resources = []) => {
    return resources.filter(r => {
      const name = (r.name || '').toString().trim()
      return !/^john\s*deo$/i.test(name)
    })
  }

  const fetchResources = async (page = 1, currentFilters = filters) => {
    setLoading(true)
    try {
      // Build filters, ignoring empty values
      const queryFilters = Object.entries(currentFilters).reduce((acc, [key, val]) => {
        if (val) acc[key] = val
        return acc
      }, {})

      const params = {
        page,
        page_size: pagination.page_size,
        ...queryFilters
      }

      const response = await companyService.getBenchResources(params)

      if (response.success) {
        const { resources, total_count, total_pages, current_page } = response.data

        const mappedResources = resources.map((res) => ({
          id: res.id,
          name: `${res.first_name || ''} ${res.last_name || ''}`.trim() || res.name,
          designation: res.designation,
          location: res.location,
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face&auto=format&q=80",
          bio: res.summary_text || `${res.experience} years of experience as ${res.designation}. Currently ${res.availability?.toLowerCase()}.`,
          primary_skills: res.primary_skills || [],
          secondary_skills: res.secondary_skills || [],
          technologies: res.technologies || [],
          skills: [...(res.primary_skills || []), ...(res.secondary_skills || [])],
          email: res.email,
          phone: res.phone,
          experience: res.experience,
          cost_per_hour: res.cost_per_hour,
          availability: res.availability,
          notice_period: res.notice_period,
          department: res.department,
          vendor_name: res.vendor_name,
          current_project: res.current_project,
          joining_date: res.joining_date,
        }))

        setAllResources(removePlaceholderResources(mappedResources))
        setPagination(prev => ({
          ...prev,
          page: response.data.current_page || page,
          total_pages: response.data.total_pages,
          total_count: total_count
        }))
      }
    } catch (err) {
      console.error(err)
      setAllResources([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!propResources) {
      fetchResources(1)
    }
  }, [propResources])

  // Resource Modal Component (rendered in a portal to avoid stacking/context issues)
  const ResourceModal = ({ resource, onClose }) => {
    const [details, setDetails] = useState(resource)
    const [isLoadingDetails, setIsLoadingDetails] = useState(true)

    useEffect(() => {
      const fetchDetails = async () => {
        try {
          const res = await companyService.getResourceFullView(resource.id)
          if (res.success && res.data) {
            setDetails(prev => ({
              ...prev,
              ...res.data,
              // Use standard fields from detailed view or fallback to prev
              bio: res.data.summary_text || prev.bio
            }))
          }
        } catch (e) {
          console.error("Failed to fetch full details", e)
        } finally {
          setIsLoadingDetails(false)
        }
      }
      fetchDetails()
    }, [resource.id])

    return createPortal(
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999
        }}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full mx-4 shadow-lg overflow-hidden max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 max-w-4xl mx-auto max-h-[80vh] overflow-y-auto">
            {isLoadingDetails ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-secondary-600 mt-4">Loading full details...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between pb-3 border-b border-secondary-200">
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold text-secondary-900">{details.name}</h2>
                    <p className="text-lg text-secondary-700 font-medium">{details.designation}</p>
                    {details.vendor_name && (
                      <p className="text-base text-secondary-600 font-medium flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {details.vendor_name}
                      </p>
                    )}
                    <p className="text-sm text-secondary-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {details.location}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full p-2 transition-colors duration-200"
                    aria-label="Close details"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Description / Bio */}
                <div className="bg-secondary-50 rounded-lg p-4">
                  <p className="text-secondary-700 leading-relaxed">
                    {details.summary_text || details.bio}
                  </p>
                </div>

                {/* Fields */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-secondary-200 hover:shadow-sm transition-shadow">
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Experience</p>
                        <p className="text-sm text-secondary-900">{details.experience || details.total_experience} years</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-secondary-200 hover:shadow-sm transition-shadow">
                      <div>
                        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Cost per Hour</p>
                        <p className="text-sm text-secondary-900 font-semibold">${details.cost_per_hour}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-secondary-200 hover:shadow-sm transition-shadow">
                      <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Department</p>
                        <p className="text-sm text-secondary-900">{details.department}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-secondary-200 hover:shadow-sm transition-shadow">
                      <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Availability</p>
                        <p className={`text-sm font-medium ${details.availability?.toLowerCase() === 'available' ? 'text-green-600' : 'text-orange-600'}`}>
                          {details.availability}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-secondary-200 hover:shadow-sm transition-shadow">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Notice Period</p>
                        <p className="text-sm text-secondary-900">{details.notice_period} Days</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-secondary-200 hover:shadow-sm transition-shadow">
                      <div className="flex-shrink-0 w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Current Project</p>
                        <p className="text-sm text-secondary-900">{details.current_project}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills */}
                <div className="space-y-3">
                  <div>
                    <h4 className="text-lg font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                      Primary Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {details.primary_skills?.map((skill, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {details.secondary_skills?.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-secondary-900 mb-3">Secondary Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {details.secondary_skills.map((skill, index) => (
                          <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-3 border-t border-secondary-200">
                  <button className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2">
                    Contact
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border border-secondary-300 text-secondary-700 font-medium rounded-lg hover:bg-secondary-50 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Favorites Modal
  const FavoritesModal = ({ onClose }) => {
    const favoriteResources = allResources.filter(r => favoriteResourceIds.has(r.id))

    return createPortal(
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-[9999]"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-slate-800 w-full max-w-md h-full shadow-2xl p-6 overflow-y-auto transform transition-transform duration-300 ease-in-out"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Heart className="w-6 h-6 text-red-500 fill-current" />
              Favorites ({favoriteResources.length})
            </h2>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {favoriteResources.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No favorites yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {favoriteResources.map(resource => (
                <div key={resource.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {resource.image ? (
                        <img src={resource.image} alt={resource.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                          <User className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">{resource.name}</h3>
                        <p className="text-xs text-slate-500">{resource.designation}</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => toggleFavorite(e, resource.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-success-600">${resource.cost_per_hour}/hr</span>
                    <button
                      onClick={() => { onClose(); setSelected(resource); }}
                      className="text-sm text-primary-600 hover:underline"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>,
      document.body
    )
  }


  // Redesigned Resource Card - clicking anywhere opens modal; show name beside icon
  const ResourceCard = ({ resource }) => {
    const open = () => { setSelected(resource); setModalLoading(false); }

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') open(); }}
        className="card p-6 flex flex-col items-start text-left hover-lift cursor-pointer group relative"
      >
        <button
          onClick={(e) => toggleFavorite(e, resource.id)}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors z-10"
        >
          <Heart
            className={`w-5 h-5 ${favoriteResourceIds.has(resource.id) ? 'fill-red-500 text-red-500' : 'text-slate-400'}`}
          />
        </button>
        <div className="flex items-center gap-4 w-full mb-4">
          {resource.image ? (
            <img
              src={resource.image}
              alt={resource.name}
              className="w-16 h-16 flex-shrink-0 rounded-full object-cover shadow-lg border-2 border-white dark:border-slate-600"
            />
          ) : (
            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-primary-500 to-accent-600 rounded-full flex items-center justify-center shadow-lg text-white">
              <User className="w-8 h-8" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1 group-hover:text-primary-600 transition-colors">{resource.name}</h3>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${resource.availability?.toLowerCase() === 'available' ? 'bg-success-500' : 'bg-warning-500'}`}></div>
              <span className={`text-sm font-medium ${resource.availability?.toLowerCase() === 'available' ? 'text-success-600' : 'text-warning-600'}`}> Available</span>
            </div>
          </div>
        </div>
        <div className="space-y-3 w-full">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 text-primary-500">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V8a2 2 0 01-2 2H8a2 2 0 01-2-2V6m8 0H8" />
              </svg>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">{resource.designation}</p>
          </div>

          {resource.vendor_name && (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 text-accent-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{resource.vendor_name}</p>
            </div>
          )}

          {resource.experience !== undefined && (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 text-warning-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">{resource.experience} years experience</p>
            </div>
          )}

          {resource.notice_period && (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 text-error-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">Availability: {resource.notice_period}</p>
            </div>
          )}

          {resource.cost_per_hour !== undefined && (
            <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700 w-full">
              <p className="text-lg font-bold text-success-600">${resource.cost_per_hour}<span className="text-sm font-normal text-slate-500">/hr</span></p>
              <button
                onClick={(e) => toggleSelection(e, resource.id)}
                className={`p-2 rounded-full transition-all duration-300 shadow-sm ${selectedResourceIds.has(resource.id)
                  ? 'bg-primary-600 text-white hover:bg-primary-700 ring-2 ring-primary-100 transform scale-110'
                  : 'bg-white border-2 border-slate-900 text-slate-900 hover:text-primary-600 hover:border-primary-600 hover:bg-primary-50'
                  }`}
                title={selectedResourceIds.has(resource.id) ? "Selected" : "Select resource"}
              >
                {selectedResourceIds.has(resource.id) ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Plus className="w-5 h-5" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Extract unique filter values for the requested fields
  const filterOptions = useMemo(() => {
    const experiences = [...new Set(allResources.map(r => r.experience).filter(v => v !== undefined && v !== null))].sort((a, b) => a - b)
    const ratings = [...new Set(allResources.map(r => r.rating).filter(v => v !== undefined && v !== null))].sort((a, b) => b - a)
    const technologies = [...new Set(allResources.flatMap(r => r.technologies || []).filter(Boolean))].sort()
    const skills = [...new Set(allResources.flatMap(r => r.skills || []).filter(Boolean))].sort()
    const costPerHours = [...new Set(allResources.map(r => r.cost_per_hour).filter(v => v !== undefined && v !== null))].sort((a, b) => a - b)
    const availability = [...new Set(allResources.map(r => r.availability).filter(Boolean))].sort()
    const departments = [...new Set(allResources.map(r => r.department).filter(Boolean))].sort()
    const designations = [...new Set(allResources.map(r => r.designation).filter(Boolean))].sort()

    return { experiences, ratings, technologies, skills, costPerHours, availability, departments, designations }
  }, [allResources])

  // Filter resources based on selected filters
  // Filter resources based on selected filters and favorites mode
  // Filter resources based on selected filters and favorites mode
  const filteredResources = useMemo(() => {
    return allResources.filter(resource => {
      // Favorites filter
      if (viewOnlyFavorites && !favoriteResourceIds.has(resource.id)) {
        return false
      }

      // We rely on backend filtering (handleSearch) to update allResources.
      // We do NOT re-filter here on the client side to avoid conflicts with backend logic.
      return true
    })
  }, [allResources, viewOnlyFavorites, favoriteResourceIds])

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      experience: '',
      rating: '',
      technologies: '',
      skills: '',
      cost_per_hour: '',
      availability: '',
      department: '',
      designation: ''
    })
  }

  // Search using server-side filtered endpoint if any filters chosen
  const handleSearch = () => {
    // Reset to page 1 on search
    fetchResources(1, filters)
  }

  const handlePageChange = (newPage) => {
    const pageNumber = Number(newPage);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      fetchResources(pageNumber, filters)
      // Scroll to top of grid or page
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(value => value !== '')

  // Disable background scroll when modal is open so only modal scrolls
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    if (selected) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [selected])

  const navigate = useNavigate()

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Header */}
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Talent Search</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">Find and connect with top talent for your projects.</p>
        </div>
        <button
          onClick={() => setViewOnlyFavorites(!viewOnlyFavorites)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-sm relative group ${viewOnlyFavorites
            ? 'bg-red-600 hover:bg-red-700 text-white border-transparent'
            : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
        >
          <Heart className={`w-5 h-5 ${favoriteResourceIds.size > 0 || viewOnlyFavorites ? 'fill-white text-white' : 'text-white'}`} />
          <span className="font-medium text-white">{viewOnlyFavorites ? 'Show All' : 'Favorites'}</span>
          {favoriteResourceIds.size > 0 && !viewOnlyFavorites && (
            <span className="ml-1 bg-white text-primary-600 text-xs font-bold px-2 py-0.5 rounded-full">
              {favoriteResourceIds.size}
            </span>
          )}
        </button>
      </div>

      {/* Floating Favorites Indicator (Left Side) */}
      {favoriteResourceIds.size > 0 && (
        <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40">
          <button
            onClick={() => setViewOnlyFavorites(true)}
            className="bg-white dark:bg-slate-800 shadow-lg border-r border-y border-slate-200 dark:border-slate-700 rounded-r-xl p-3 flex flex-col items-center gap-1 hover:pr-4 transition-all duration-300 group"
          >
            <Heart className="w-6 h-6 text-red-500 fill-red-500 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-bold text-slate-900 dark:text-white">{favoriteResourceIds.size}</span>
          </button>
        </div>
      )}

      {/* Floating Request Button */}
      {selectedResourceIds.size > 0 && (
        <div className="fixed bottom-8 right-8 z-50 animate-bounce-in">
          <button
            onClick={() => {
              const selectedResources = allResources.filter(r => selectedResourceIds.has(r.id))
              navigate('/dashboard/company/talent/request', { state: { selectedResources } })
            }}
            className="group flex items-center gap-3 bg-primary-600 hover:bg-primary-700 text-white pl-5 pr-2 py-3 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
          >
            <span className="font-semibold text-lg">Request Talent</span>
            <span className="bg-white text-primary-600 text-sm font-bold flex items-center justify-center w-8 h-8 rounded-full shadow-inner">
              {selectedResourceIds.size}
            </span>
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Filters</h2>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Location */}
          <div>
            <label className="label mb-2">Location</label>
            <input
              type="text"
              value={filters.location}
              onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
              placeholder="e.g. Hyderabad"
              className="input w-full"
            />
          </div>

          {/* Experience */}
          <div>
            <label className="label mb-2">Experience (Years)</label>
            <select
              value={filters.experience}
              onChange={(e) => setFilters(prev => ({ ...prev, experience: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.experiences.map(exp => (
                <option key={exp} value={String(exp)}>{exp} years</option>
              ))}
            </select>
          </div>

          {/* Rating */}
          <div>
            <label className="label mb-2">Rating</label>
            <select
              value={filters.rating}
              onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.ratings.map(r => (
                <option key={r} value={String(r)}>{r}</option>
              ))}
            </select>
          </div>

          {/* Technologies */}
          <div>
            <label className="label mb-2">Technologies</label>
            <select
              value={filters.technologies}
              onChange={(e) => setFilters(prev => ({ ...prev, technologies: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.technologies.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Skills */}
          <div>
            <label className="label mb-2">Skills</label>
            <select
              value={filters.skills}
              onChange={(e) => setFilters(prev => ({ ...prev, skills: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.skills.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Cost per hour */}
          <div>
            <label className="label mb-2">Cost per Hour</label>
            <select
              value={filters.cost_per_hour}
              onChange={(e) => setFilters(prev => ({ ...prev, cost_per_hour: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.costPerHours.map(c => (
                <option key={c} value={String(c)}>${c}</option>
              ))}
            </select>
          </div>

          {/* Availability */}
          <div>
            <label className="label mb-2">Availability</label>
            <select
              value={filters.availability}
              onChange={(e) => setFilters(prev => ({ ...prev, availability: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.availability.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          {/* Department */}
          <div>
            <label className="label mb-2">Department</label>
            <select
              value={filters.department}
              onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.departments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Designation */}
          <div>
            <label className="label mb-2">Designation</label>
            <select
              value={filters.designation}
              onChange={(e) => setFilters(prev => ({ ...prev, designation: e.target.value }))}
              className="input appearance-none"
            >
              <option value="">Any</option>
              {filterOptions.designations.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-secondary-200 dark:border-slate-700">
          <div>
            {hasActiveFilters && (
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing <span className="font-semibold text-secondary-900 dark:text-white">{filteredResources.length}</span> of <span className="font-semibold text-secondary-900 dark:text-white">{pagination.total_count}</span> resources
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { clearFilters(); handleSearch() }}
              className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="bg-primary-600 disabled:opacity-60 text-white px-4 py-2 rounded-lg"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div >

      {/* Resource Grid */}
      {
        loading ? (
          <div className="text-center py-8">Loading bench resources...</div>
        ) : filteredResources.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-secondary-200 dark:border-slate-700">
            <p className="text-lg text-secondary-600 dark:text-secondary-400 mb-2">No resources found</p>
            <p className="text-sm text-secondary-500 dark:text-secondary-500">
              {hasActiveFilters ? 'Try adjusting your filters' : 'No resources available at the moment'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {filteredResources.map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>
        )
      }

      {/* Pagination Controls */}
      {!loading && pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-1 mx-2">
            {[...Array(pagination.total_pages)].map((_, i) => {
              const p = i + 1
              // Simple pagination logic: show all if <= 7, otherwise condensed logic could be added here.
              // For now, assuming manageable number of pages as per user context ("3 page")
              return (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${pagination.page === p
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  {p}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.total_pages}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Details Modal */}
      {
        selected && (
          <ResourceModal
            resource={selected}
            onClose={() => setSelected(null)}
            loading={modalLoading}
          />
        )
      }

      {/* Favorites Sidebar Modal */}
      {showFavorites && (
        <FavoritesModal onClose={() => setShowFavorites(false)} />
      )}
    </div >
  )
}

export default TalentSearch
